import chalk from 'chalk';
import ora from 'ora';
import { loadConfig, getDefaultConfig } from '../../engine/config-loader.js';
import { resolveRules, groupRulesByTool } from '../../engine/rule-engine.js';
import {
  loadBaseline,
  saveBaseline,
  createBaseline,
  updateBaseline,
  getBaselineStats,
} from '../../engine/baseline.js';
import { gitleaksRunner, semgrepRunner, osvScannerRunner, runBuiltinChecks } from '../../tools/index.js';
import type { CheckResult } from '../../types/index.js';

interface BaselineOptions {
  update?: boolean;
  show?: boolean;
}

export async function baselineCommand(options: BaselineOptions): Promise<void> {
  const cwd = process.cwd();
  const spinner = ora('Loading configuration...').start();

  try {
    const loaded = await loadConfig(cwd);
    const config = loaded?.config ?? getDefaultConfig();

    // Load existing baseline
    const existingBaseline = await loadBaseline(cwd);

    if (options.show) {
      spinner.stop();
      await showBaselineStatus(cwd, existingBaseline);
      return;
    }

    // Run full scan
    spinner.text = 'Running full scan...';
    const allResults = await runFullScan(cwd, config);

    if (existingBaseline && !options.update) {
      // Show comparison
      spinner.stop();
      const stats = getBaselineStats(allResults, existingBaseline);

      console.log(chalk.bold('\n📊 Baseline Status:\n'));
      console.log(`  Total issues:     ${chalk.cyan(stats.total)}`);
      console.log(`  ${chalk.green('Fixed since baseline:')} ${stats.fixed}`);
      console.log(`  ${chalk.yellow('Baselined (ignored):')} ${stats.baselined}`);
      console.log(`  ${chalk.red('New issues:')}          ${stats.new}`);

      if (stats.new > 0) {
        console.log(chalk.red(`\n⚠️  ${stats.new} new issue(s) detected!`));
        console.log(chalk.dim('Run `devrail baseline --update` to add them to baseline.'));
      } else if (stats.fixed > 0) {
        console.log(chalk.green(`\n✓ ${stats.fixed} issue(s) fixed since baseline!`));
        console.log(chalk.dim('Run `devrail baseline --update` to update the baseline.'));
      } else {
        console.log(chalk.green('\n✓ No changes since baseline.'));
      }

      console.log(chalk.dim(`\nBaseline: .devrail/baseline.json (${existingBaseline.entries.length} entries)\n`));
      return;
    }

    // Create or update baseline
    let baseline;
    if (existingBaseline && options.update) {
      spinner.text = 'Updating baseline...';
      baseline = updateBaseline(existingBaseline, allResults);
    } else {
      spinner.text = 'Creating baseline...';
      baseline = createBaseline(allResults);
    }

    await saveBaseline(cwd, baseline);

    spinner.succeed(chalk.green('Baseline saved!'));

    console.log(chalk.bold('\n📋 Baseline Summary:\n'));
    console.log(`  Total issues baselined: ${chalk.cyan(baseline.entries.length)}`);
    console.log(`  Created: ${chalk.dim(baseline.createdAt)}`);

    const byCategory = new Map<string, number>();
    for (const entry of baseline.entries) {
      const category = entry.ruleId.split('.')[0] ?? 'other';
      byCategory.set(category, (byCategory.get(category) ?? 0) + 1);
    }

    console.log('\n  By category:');
    for (const [category, count] of byCategory) {
      console.log(`    ${category}: ${count}`);
    }

    console.log(chalk.bold('\n🚀 What this means:\n'));
    console.log('  • These issues are now "accepted" and won\'t fail CI');
    console.log('  • New issues in the same categories WILL fail CI');
    console.log('  • Fix baselined issues over time to improve your score');

    console.log(chalk.dim('\nRun `devrail ci` to verify only new issues are blocked.\n'));
  } catch (error) {
    spinner.fail(chalk.red('Baseline failed'));
    console.error(error);
    process.exit(1);
  }
}

async function showBaselineStatus(cwd: string, baseline: ReturnType<typeof loadBaseline> extends Promise<infer T> ? T : never): Promise<void> {
  if (!baseline) {
    console.log(chalk.yellow('\n⚠️  No baseline found.'));
    console.log(chalk.dim('Run `devrail baseline` to create one.\n'));
    return;
  }

  console.log(chalk.bold('\n📋 Current Baseline:\n'));
  console.log(`  Entries:  ${chalk.cyan(baseline.entries.length)}`);
  console.log(`  Created:  ${chalk.dim(baseline.createdAt)}`);
  console.log(`  Updated:  ${chalk.dim(baseline.updatedAt)}`);

  const byRule = new Map<string, number>();
  for (const entry of baseline.entries) {
    byRule.set(entry.ruleId, (byRule.get(entry.ruleId) ?? 0) + 1);
  }

  console.log('\n  Top issues:');
  const sorted = [...byRule.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  for (const [rule, count] of sorted) {
    console.log(`    ${chalk.dim(rule)}: ${count}`);
  }

  console.log();
}

async function runFullScan(cwd: string, config: ReturnType<typeof getDefaultConfig>): Promise<CheckResult[]> {
  const allResults: CheckResult[] = [];

  // Builtin checks
  const builtinResults = await runBuiltinChecks(cwd, [
    'secrets.gitignore-required',
    'secrets.no-env-commit',
    'deps.lockfile.required',
    'deps.no-unpinned',
    'deps.no-git-deps',
    'tests.unit.required',
    'code.strict-mode',
    'config.node-version',
    'config.editor-config',
  ]);
  allResults.push(...builtinResults);

  // External tools
  if (config.tools?.gitleaks !== false && (await gitleaksRunner.isInstalled())) {
    const results = await gitleaksRunner.run(cwd);
    allResults.push(...results);
  }

  if (config.tools?.osvScanner !== false && (await osvScannerRunner.isInstalled())) {
    const results = await osvScannerRunner.run(cwd);
    allResults.push(...results);
  }

  if (config.tools?.semgrep !== false && (await semgrepRunner.isInstalled())) {
    const results = await semgrepRunner.run(cwd);
    allResults.push(...results);
  }

  return allResults;
}
